import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { CreateUserRequest } from "@/lib/types";

export function useUsers() {
  return useQuery({
    queryKey: ["users"],
    queryFn: () => api.listUsers(),
  });
}

// Roster for non-admin user pickers. Own query key — the mutations below
// invalidate `["users"]`, which this endpoint has nothing to do with.
export function useUserDirectory(enabled = true) {
  return useQuery({
    queryKey: ["user-directory"],
    queryFn: () => api.listUserDirectory(),
    enabled,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateUserRequest) => api.createUser(body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useUpdateSshKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, sshPubKey }: { userId: number; sshPubKey: string }) =>
      api.updateSshKey(userId, sshPubKey),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["users"] }),
  });
}

export function useUpdateAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, avatarUrl }: { userId: number; avatarUrl: string | null }) =>
      api.updateAvatar(userId, avatarUrl),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users"] });
      void queryClient.invalidateQueries({ queryKey: ["machines"] });
      void queryClient.invalidateQueries({ queryKey: ["cbg-machines"] });
      void queryClient.invalidateQueries({ queryKey: ["k8s-clusters"] });
    },
  });
}

export function useApiKeys(userId: number) {
  return useQuery({
    queryKey: ["api-keys", userId],
    queryFn: () => api.listApiKeys(userId),
    enabled: userId > 0,
  });
}

export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, name }: { userId: number; name: string }) =>
      api.createApiKey(userId, name),
    onSuccess: (_data, vars) => void qc.invalidateQueries({ queryKey: ["api-keys", vars.userId] }),
  });
}

export function useRevokeApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, keyId }: { userId: number; keyId: number }) =>
      api.revokeApiKey(userId, keyId),
    onSuccess: (_data, vars) => void qc.invalidateQueries({ queryKey: ["api-keys", vars.userId] }),
  });
}

export function useRevealApiKey() {
  return useMutation({
    mutationFn: ({ userId, keyId }: { userId: number; keyId: number }) =>
      api.revealApiKey(userId, keyId),
  });
}
